
import PageHeader from "app/shared/PageHeader";
import "./Dashboard.scss";
import CustomReconTabs from "../Reconcilation/components/CustomReconTabs";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import {
  onboardingMessageShown,
  tabViewStates,
  userInfo,
  workspaceReports,
} from "app/config/States/users";
import Loader from "app/shared/Loader";
import { Empty, Modal, Tabs, Button, Table, Tooltip } from "antd";
import { useNavigate, useRouteError } from "react-router-dom";
import EmptyState from "app/shared/EmptyState";
import { ExclamationCircleFilled, WarningFilled, ExclamationCircleOutlined } from "@ant-design/icons";
import { getSubdomain } from "app/utils/generic.helper";
import { apiGet } from "app/services/apiServices";
import {
  DASHBOARD_LINK_ZOHO_CRITERIA_FILTER,
  dashboardTabConfig,
  dashboardURLConfig,
  dashboardWorkspaceTabConfig,
  dashboardWorkspaceURLConfig,
} from "global.constants";
import { Mixpanel } from "app/config/Mixpanel";
import Typography from "app/shared/Typography";
import { MarginTwoTone } from "@mui/icons-material";
import DescriptionIcon from "@mui/icons-material/Description";
import { colorPicker } from "app/utils/color.helper";
import { FinkAnalytics } from "app/utils/GoogleAnalytics";
import { PostHogProvider } from 'posthog-js/react'
import posthog from 'posthog-js';
import ZAViewSelector from "app/shared/ZAViewSelector";
export default function Dashboard(props: any) {
  const [activeKey, setActiveKey] = useState("1");
  const [userDetails, _] = useRecoilState<any>(userInfo);
  const [tabViews, setTabView] = useRecoilState<any>(tabViewStates);
  const [reports, setWorkspaceReports] = useRecoilState<any>(workspaceReports);
  const [onboardingMessage, setShowOnboardingMessage] = useRecoilState<any>(
    onboardingMessageShown
  );
  const [isLoading, setLoading] = useState(false);
  const [dashboardURL, setDashboardURL] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showCriticalModal, setShowCriticalModal] = useState(false);
  const [credentialsData, setCredentialsData] = useState<any>(null);
  const navigate = useNavigate();

  // Helper functions to manage modal shown/dismissal in localStorage (once per day per user/email)
  // Uses email to ensure each user sees modal independently, even in same workspace
  const getUserIdentifier = (userId: string | number, email?: string): string => {
    // Use email if available (more stable), otherwise fallback to userId
    if (email) {
      return `email_${email.toLowerCase()}`;
    }
    return `user_${String(userId)}`;
  };

  const getModalShownDate = (userId: string | number, email?: string): string | null => {
    if (!userId && !email) return null;
    const identifier = getUserIdentifier(userId, email);
    const key = `criticalModalShown_${identifier}`;
    return localStorage.getItem(key);
  };

  const setModalShownDate = (userId: string | number, email?: string) => {
    if (!userId && !email) return;
    const identifier = getUserIdentifier(userId, email);
    const key = `criticalModalShown_${identifier}`;
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    localStorage.setItem(key, today);
  };

  const getModalDismissalDate = (userId: string | number, email?: string): string | null => {
    if (!userId && !email) return null;
    const identifier = getUserIdentifier(userId, email);
    const key = `criticalModalDismissed_${identifier}`;
    return localStorage.getItem(key);
  };

  const setModalDismissalDate = (userId: string | number, email?: string) => {
    if (!userId && !email) return;
    const identifier = getUserIdentifier(userId, email);
    const key = `criticalModalDismissed_${identifier}`;
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    localStorage.setItem(key, today);
  };

  const wasModalShownToday = (userId: string | number, email?: string): boolean => {
    if (!userId && !email) {
      console.log('No userId or email provided for shown check');
      return false;
    }
    const shownDate = getModalShownDate(userId, email);
    if (!shownDate) {
      console.log('No shown date found for user:', { userId, email });
      return false;
    }
    
    const today = new Date().toISOString().split('T')[0];
    const wasShown = shownDate === today;
    return wasShown;
  };

  const isModalDismissedToday = (userId: string | number, email?: string): boolean => {
    if (!userId && !email) {
      console.log('No userId or email provided for dismissal check');
      return false;
    }
    const dismissalDate = getModalDismissalDate(userId, email);
    if (!dismissalDate) {
      console.log('No dismissal date found for user:', { userId, email });
      return false;
    }
    
    const today = new Date().toISOString().split('T')[0];
    const isDismissed = dismissalDate === today;
    return isDismissed;
  };

  Mixpanel.track("User Landed On Dashboard", { Screen: "Dashboard" });
  const domain: any = getSubdomain();

  posthog.init('phc_sqVedcbiO1p6MDNKAwkqNokw9vWJd73mwr8c9CEUn1P',
    {
        api_host: 'https://us.i.posthog.com',
        person_profiles: 'identified_only' ,
        capture_pageview: true,
        debug: true,
    }
  )

  useEffect(() => {
    setLoading(true);
  
    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === "localhost" || hostname === "127.0.0.1" || hostname === "";
    const subdomain = hostname.split(".")[0];
  
    const email = userDetails?.email?.toLowerCase();
    const blockedDomains = ["kgrp.in", "finkraft.ai"];
    const userDomain = email?.split("@")[1];
  
    const isBlocked = blockedDomains.includes(userDomain);
  
    if (!isLocalhost && userDetails?.id && email) {
      if (!isBlocked) {
        // ✅ Allow PostHog tracking
        posthog.identify(userDetails.id, {
          name: userDetails.name,
          email: userDetails.email,
          workspace_name: userDetails.currentWorkspace?.name,
          subdomain,
        });
  
        posthog.capture("user_details_captured", {
          name: userDetails.name,
          email: userDetails.email,
          workspace_name: userDetails.currentWorkspace?.name,
          subdomain,
          access_date: new Date().toISOString().split("T")[0],
        });
      } else {
        posthog.reset(); // clears cookies & identity
      }
    }
  
    // Google Analytics tracking stays (if you still want it)
    if (userDetails?.id && typeof window.gtag === "function") {
      window.gtag("config", "G-YGHTMQESH6", {
        user_id: userDetails.id,
      });
    }
  
    // ... keep your FinkAnalytics call here if needed
  
    setTimeout(() => {
      setLoading(false);
      if (!onboardingMessage && subdomain === "app") setShowModal(true);
    }, 2000);

    // Call API to fetch user credentials alert
    fetchUserCreds();
  }, [userDetails]);

  // Show critical modal when credential issues are detected (once per day per email)
  // If dismissed today, won't show again today, but will show again tomorrow
  useEffect(() => {
    if (credentialsData && userDetails?.id && userDetails?.email) {
      const userId = userDetails.id;
      const userEmail = userDetails.email;
      
      // Check if modal was dismissed today - if dismissed, don't show again today
      // Tomorrow, the date will be different, so it will show again
      const isDismissedToday = isModalDismissedToday(userId, userEmail);
      
      if (isDismissedToday) {
        setShowCriticalModal(false);
        return;
      }
      
      // Check if modal was already shown today - if shown, don't show again (prevents multiple shows in same session)
      const wasShownToday = wasModalShownToday(userId, userEmail);
      
      if (wasShownToday) {
        setShowCriticalModal(false);
        return;
      }
      
      // Modal hasn't been shown or dismissed today, check if we should show it
      const totalIssues =
        (credentialsData.invalid_gstin || 0) +
        (credentialsData.unknown_gstin || 0) +
        (credentialsData.invalid_airline || 0) +
        (credentialsData.not_available_airline || 0) +
        (credentialsData.invalid_email || 0) +
        (credentialsData.notAvailableEmail || 0);
      
      if (totalIssues > 0) {
        setModalShownDate(userId, userEmail);
        setShowCriticalModal(true);
      } else {
        setShowCriticalModal(false);
      }
    } else {
      console.log('Conditions not met for modal:', { 
        hasCredentialsData: !!credentialsData, 
        hasUserId: !!userDetails?.id,
        hasEmail: !!userDetails?.email
      });
    }
  }, [credentialsData, userDetails?.id, userDetails?.email]);

  // Reset modal state if it was dismissed today (safeguard)
  // Note: We don't check for "shown" here because we want to show it once per day
  useEffect(() => {
    if ((userDetails?.id || userDetails?.email) && showCriticalModal) {
      const userId = userDetails.id;
      const userEmail = userDetails.email;
      const isDismissedToday = isModalDismissedToday(userId, userEmail);
      if (isDismissedToday) {
        setShowCriticalModal(false);
      }
    }
  }, [userDetails?.id, userDetails?.email, showCriticalModal]);

  const fetchUserCreds = async () => {
    try {
      const response = await apiGet("https://stage-apiplatform.finkraftai.com/api/meta/user/credentialsAlert");
      if (response.status && response.data) {
        setCredentialsData(response.data);
      } else {
        setCredentialsData(null);
      }
    } catch (error) {
      setCredentialsData(null);
    }
  };

  const getFormattedURL = (url: any) => {
    // Return null if URL is empty or not configured
    if (!url || url.trim() === "") {
      return null;
    }
    if (userDetails.currentWorkspace?.meta?.full_access) {
      return url;
    }
    const workspaceName = userDetails.currentWorkspace?.name;
    const encodedWorkspaceName = encodeURIComponent(workspaceName);

    // Check workspace-specific override first
    const workspaceOverride = dashboardWorkspaceURLConfig[domain]?.[workspaceName]?.[activeKey];
    if (workspaceOverride) {
      if (!workspaceOverride.filterKey) return workspaceOverride.url;
      return `${workspaceOverride.url}?ZOHO_CRITERIA=%22${workspaceOverride.filterKey}%22%3D%27${encodedWorkspaceName}%27`;
    }

    // Default filter
    const filterKey =
      DASHBOARD_LINK_ZOHO_CRITERIA_FILTER[domain]?.[activeKey] || "workspace";
    return `${url}?ZOHO_CRITERIA=%22${filterKey}%22%3D%27${encodedWorkspaceName}%27`;
  };

  const getDashboardOptionVisible = (options: any) => {
    const menus = Array.isArray(options)
      ? options.filter(
          (menu: any) =>
            !menu.adminOnly || userDetails.currentWorkspace?.meta?.full_access
        )
      : [];

    if (domain === "app") {
      menus.push({ key: "3", label: "Dashboard - Credential" });
    }

    return menus;
  };

  posthog.capture('my event', { property: 'value' })

  posthog.capture('user_clicked_invoice_download', {
    workspace: 'MMT',
    location: 'flights_page',
  });

const workspaceName = userDetails.currentWorkspace?.name?.trim();
const workspaceTabOverride = dashboardWorkspaceTabConfig[domain]?.[workspaceName];

  // let initialMenus: any =
  //   getDashboardOptionVisible(dashboardTabConfig[domain]) || [];
  let initialMenus: any =
  getDashboardOptionVisible(workspaceTabOverride || dashboardTabConfig[domain]) || [];

  const handleRedirectOnboarding = () => {
    setShowOnboardingMessage(true);
    setActiveKey("3");
    setShowModal(false);
  };

  const handleRemindLater = () => {
    setShowCriticalModal(false);
    
    // Save dismissal date for this email (once per day per email)
    const userId = userDetails?.id;
    const userEmail = userDetails?.email;
    if (userId || userEmail) {
      setModalDismissalDate(userId, userEmail);
    }

    // PostHog tracking for "Remind me later" button
    const hostname = window.location.hostname;
    const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "";
    
    // Calculate total issues
    const totalIssues = credentialsData ?
      (credentialsData.invalid_gstin || 0) +
      (credentialsData.unknown_gstin || 0) +
      (credentialsData.invalid_airline || 0) +
      (credentialsData.not_available_airline || 0) +
      (credentialsData.invalid_email || 0) +
      (credentialsData.notAvailableEmail || 0) : 0;
    
    if (!isLocalhost && userDetails?.email) {
      const email = userDetails.email.toLowerCase();
      const blockDomains = ["kgrp.in", "finkraft.ai"];
      const userDomain = email.split("@")[1];
      
      if (!blockDomains.includes(userDomain)) {
        const subdomain = hostname.split(".")[0];
        const workspaceId = userDetails?.currentWorkspace?.id;
        
        const eventData = {
          workspace_id: workspaceId,
          workspace_name: userDetails.currentWorkspace?.name,
          user_id: userDetails.id,
          user_email: userDetails.email,
          subdomain: subdomain,
          total_issues: totalIssues,
          invalid_gstin: credentialsData?.invalid_gstin || 0,
          unknown_gstin: credentialsData?.unknown_gstin || 0,
          invalid_airline: credentialsData?.invalid_airline || 0,
          not_available_airline: credentialsData?.not_available_airline || 0,
          invalid_email: credentialsData?.invalid_email || 0,
          not_available_email: credentialsData?.notAvailableEmail || 0,
          timestamp: new Date().toISOString(),
        };
        
        posthog.capture("critical_credentials_modal_remind_later_clicked", eventData);
      }
    }
  };

  const handleGoToCredentials = () => {
    setShowCriticalModal(false);
    
    // Save dismissal date for this email (once per day per email)
    const userId = userDetails?.id;
    const userEmail = userDetails?.email;
    if (userId || userEmail) {
      setModalDismissalDate(userId, userEmail);
    }
    
    navigate("/integrations");
  };

  // Map API response data to table data
  const credentialIssuesData = credentialsData ? [
    {
      key: "1",
      credential: "GST",
      total: (credentialsData.invalid_gstin || 0) + (credentialsData.unknown_gstin || 0),
      wrongCredentials: credentialsData.invalid_gstin || 0,
      notAvailable: credentialsData.unknown_gstin || 0,
    },
    {
      key: "2",
      credential: "Airline",
      total: (credentialsData.invalid_airline || 0) + (credentialsData.not_available_airline || 0),
      wrongCredentials: credentialsData.invalid_airline || 0,
      notAvailable: credentialsData.not_available_airline || 0,
    },
    {
      key: "3",
      credential: "SSR Email",
      total: (credentialsData.invalid_email || 0) + (credentialsData.notAvailableEmail || 0),
      wrongCredentials: credentialsData.invalid_email || 0,
      notAvailable: credentialsData.notAvailableEmail || 0,
    },
  ] : [];

  // Calculate total issues count (sum of all "total" values from the table)
  const totalIssuesCount = credentialIssuesData.reduce((sum, item) => sum + (item.total || 0), 0);

  // Helper functions for table styling
  const getHeaderCellStyle = () => ({
    style: {
      backgroundColor: "transparent",
      background: "transparent",
      color: "#BDBDBD",
      fontSize: "12px",
    },
  });

  const getCellStyle = (index?: number) => ({
    style: {
      borderBottom: index === 0 || index === 1 ? "1px solid #f0f0f0" : "none",
    },
  });

  const getHeaderTitle = (text: string) => (
    <span style={{ color: "#BDBDBD", fontSize: "12px" }}>{text}</span>
  );

  const credentialIssuesColumns = [
    {
      title: getHeaderTitle("Credentials"),
      dataIndex: "credential",
      key: "credential",
      onHeaderCell: getHeaderCellStyle,
      onCell: (_record: any, index?: number) => getCellStyle(index),
    },
    {
      title: getHeaderTitle("No."),
      dataIndex: "total",
      key: "total",
      onHeaderCell: getHeaderCellStyle,
      onCell: (_record: any, index?: number) => getCellStyle(index),
    },
    {
      title: getHeaderTitle("Wrong Credentials"),
      dataIndex: "wrongCredentials",
      key: "wrongCredentials",
      onHeaderCell: getHeaderCellStyle,
      onCell: (_record: any, index?: number) => getCellStyle(index),
    },
    {
      title: getHeaderTitle("Not Available"),
      dataIndex: "notAvailable",
      key: "notAvailable",
      onHeaderCell: getHeaderCellStyle,
      onCell: (_record: any, index?: number) => getCellStyle(index),
    },
    {
      title: "",
      key: "action",
      render: (_text: any, record: any) => {
        return (
          <Tooltip>
            <ExclamationCircleOutlined style={{ color: "#ff4d4f", cursor: "pointer" }} />
          </Tooltip>
        );
      },
      onHeaderCell: () => ({
        style: {
          backgroundColor: "transparent",
          background: "transparent",
        },
      }),
      onCell: (_record: any, index?: number) => getCellStyle(index),
    },
  ];
  return isLoading ? (
    <Loader />
  ) : (
    <div className="Dashboard">
      <div className="ScreenContainer">
        <PageHeader
          leftActions={
            <div style={{ display: "flex", alignItems: "center" }}>
            {/* <ZAViewSelector
              activeKey={activeKey === "Credential" ? "" : activeKey}
              items={reports?.dashboards || []}
              onViewChange={(data: any, fullObj: any) => setActiveKey(data)}
            /> */}
            <div style={{ marginLeft: 12 }}>
             <Tabs
              items={initialMenus}
              tabBarStyle={{
                borderBottom: "none", // Remove the underline border
                margin: 0,
              }}
              activeKey={activeKey}
              onChange={(tabItem: string) => setActiveKey(tabItem)}
            />
            </div>
          </div>
            // <Tabs
            //   items={initialMenus}
            //   tabBarStyle={{
            //     borderBottom: "none", // Remove the underline border
            //     margin: 0,
            //   }}
            //   activeKey={activeKey}
            //   onChange={(tabItem: string) => setActiveKey(tabItem)}
            // />
          }
          // leftActions={
          //   <CustomReconTabs
          //     activeKey={activeKey}
          //     title="Report"
          //     onTabChange={(tabKey: any) => setActiveKey(tabKey)}
          //     onTabModify={(tabs: any) =>
          //       setTabView({ ...tabViews, dashboard: tabs })
          //     }
          //     hideAddTab
          //     hideRemove
          //     defaultTabs={getTabView()}
          //     width={200}
          //   />
          // }
          rightActions={
            // (userDetails?.email?.includes("finkraft.ai") ||
            //   userDetails?.email?.includes("kgrp.in")) ? (
              <Button
                size="small"
                icon={<DescriptionIcon style={{ fontSize: 14, color: colorPicker("primary.700") }} />}
                style={{ borderColor: colorPicker("primary.700"), color: colorPicker("primary.700") }}
                onClick={() => navigate("/reports")}
              >
                Reports
              </Button>
            // ) : null
          }
        />

        <div className="TableContainer">
          {getFormattedURL(dashboardURLConfig[domain]?.[activeKey]) ? (
            <iframe
              height="100%"
              style={{ width: "100%" }}
              scrolling="no"
              title="Iframe"
              id="dashboardFrame"
              src={getFormattedURL(dashboardURLConfig[domain]?.[activeKey])}
            ></iframe>
          ) : (
            <EmptyState
              title="No Dashboards Available"
              description="Try changing workspace for dashboard view"
            />
          )}
        </div>
      </div>

      <Modal
        title={false}
        footer={false}
        open={showModal}
        closable={false}
        width={500}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            {" "}
            <ExclamationCircleFilled
              style={{ marginRight: 4, color: "#e67e22" }}
            />
            Require immediate action
          </Typography>
          <Typography
            variant="xs"
            style={{ color: "#7e7979", textAlign: "center", marginTop: 6 }}
          >
            Onboarding steps pending , you will be redirected to on boarding
            dashboard review the data sets which are missing then go to
            integration tab and complete the pending items.{" "}
            <strong
              style={{ cursor: "pointer", color: colorPicker("primary.700") }}
              onClick={handleRedirectOnboarding}
            >
              Complete Now
            </strong>
          </Typography>
        </div>
      </Modal>

      <Modal
        title={
          <Typography variant="h6" style={{ fontWeight: 600, color: "#181D1F", fontSize: "20px", lineHeight: '120%' }}>
            Critical Account Status
          </Typography>
        }
        open={showCriticalModal}
        onCancel={() => {
          setShowCriticalModal(false);
          
          // Save dismissal date for this email (once per day per email)
          const userId = userDetails?.id;
          const userEmail = userDetails?.email;
          if (userId || userEmail) {
            setModalDismissalDate(userId, userEmail);
          }
        }}
        closable={true}
        width={700}
        centered
        footer={null}
        className="critical-account-modal"
      >
        <div style={{ padding: "8px 0" }}>
          {/* Warning Section */}
          <div
            style={{
              border: "2px #ff4d4f",
              borderRadius: "4px",
              padding: '8px 16px',
              marginBottom: "24px",
              height: '80px',
              background: 'rgba(231, 0, 11, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '4px',
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: '8px',
              }}
            >
              <WarningFilled
                style={{
                  color: "#EC221F", fontSize: "20px", width: '20.527px',
                  height: '18px'
                }}
              />
              <Typography
                variant="body1"
                style={{
                  fontWeight: 600, color: ' #EC221F', fontSize: '14px',
                  lineHeight: '24px', fontFamily:"Noto Sans"
                }}
              >
                Issues found ({totalIssuesCount})
              </Typography>
            </div>

            <Typography
              variant="body2"
              style={{
                color: "#EC221F", fontSize: '12px', lineHeight: '16px', fontWeight: 500,
                letterspacing: '-0.024px',marginLeft:"27px",fontFamily:"Noto Sans"
              }}
            >
              Failing to update these credentials immediately will prevent core
              functionality and compromise compliance. Please click 'Go to Credentials and Fix now' to resolve.
            </Typography>
          </div>

          {/* Breakdown Section */}
          <div style={{ marginBottom: "24px" ,color: '#181D1F',fontSize: '16px', lineHeight: '24px', fontWeight: 600 }}>
            <Typography
              variant="body1"
              style={{ fontWeight: 600, marginBottom: "16px", fontFamily:"Noto Sans" }}
            >
              Breakdown of Issues ({totalIssuesCount})
            </Typography>
            <div className="credential-issues-table-wrapper">
              <Table
                dataSource={credentialIssuesData}
                columns={credentialIssuesColumns}
                pagination={false}
                size="small"
                bordered={false}
                style={{ marginBottom: "16px" , fontFamily:"Noto Sans"}}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "24px",
              padding: '0 16px',
            }}
          >
            <Button
              onClick={handleRemindLater}
              style={{
                border: "1px solid #03A5B7",
                color: "#03A5B7",
                fontSize: '14px',
                 fontFamily:"Noto Sans"
              }}
            >
              Remind me later
            </Button>
            <Button
              type="primary"
              onClick={handleGoToCredentials}
              style={{
                background: '#03A5B7',
                padding: '0 16px',
                 fontFamily:"Noto Sans"
              }}
            >
              Go to Credentials and Fix now
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
